import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Typography,
  Card,
  Grid,
  Tabs,
} from "antd";
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  UserOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from "@ant-design/icons";

import apiPath from "../../constants/apiPath";
import { AppStateContext } from "../../context/AppContext";
import { AuthContext } from "../../context/AuthContext";
import encryptDecrypt from "../../helper/encryptDecrypt";
import { Severty, ShowToast } from "../../helper/toast";
import useRequest from "../../hooks/useRequest";
console.log("hellooo");
const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

const theme = {
  primary: "var(--theme1, #1f4e79)",
  primaryDark: "var(--theme1Dark, #163a5a)",
  muted: "rgba(0, 0, 0, 0.45)",
  border: "rgba(22, 58, 90, 0.18)",
};

const extractToken = (data) =>
  data?.token ||
  data?.accessToken ||
  data?.jwt ||
  data?.data?.token ||
  data?.data?.accessToken ||
  data?.Details?.token ||
  null;

const ROLE_CONFIG = {
  admin: {
    key: "admin",
    label: "Admin",
    userType: "Super_Admin",
    title: "Admin Portal",
    subtitle: "Manage operations with secure administrator access",
    emailLabel: "Admin email",
    emailPlaceholder: "Enter admin email address",
    submitText: "Access admin panel",
    loginPath: apiPath.adminLogin,
    homePath: "/dashboard",
  },
  hotel: {
    key: "hotel",
    label: "Hotel",
    userType: "HotelPartner",
    title: "Hotel Portal",
    subtitle: "Manage properties, bookings, and teams securely",
    emailLabel: "Hotel email",
    emailPlaceholder: "Enter hotel manager email",
    submitText: "Access hotel panel",
    loginPath: apiPath.hotelLogin,
    homePath: "/dashboard",
  },
  agent: {
    key: "agent",
    label: "Agent",
    userType: "Agent",
    title: "Agent Portal",
    subtitle: "Handle booking and guest operations securely",
    emailLabel: "Agent email",
    emailPlaceholder: "Enter agent email address",
    submitText: "Access agent panel",
    loginPath: apiPath.agentLogin,
    homePath: "/agent-dashboard",
  },
  airline: {
    key: "airline",
    label: "Airline",
    userType: "Airline",
    title: "Airline Portal",
    subtitle: "Manage airline and linked hotel details",
    emailLabel: "Airline email",
    emailPlaceholder: "Enter airline user email",
    submitText: "Access airline panel",
    loginPath: "/airline/login",
    homePath: "/airline-dashboard",
  },
  bus: {
    key: "bus",
    label: "Bus Company",
    userType: "bus_company",
    title: "Bus Company Portal",
    subtitle: "Manage passenger transport and trip reports securely",
    emailLabel: "Company email",
    emailPlaceholder: "Enter bus company email",
    submitText: "Access bus panel",
    loginPath: apiPath.adminLogin,
    homePath: "/bus-dashboard",
  },
};

const resolveUserDetails = (data) =>
  data?.adminDetails ||
  data?.managerDetails ||
  data?.agentDetails ||
  data?.airlineDetails ||
  data?.busCompanyDetails ||
  data?.Details ||
  data?.data?.user ||
  null;

const labelIcon = (icon, color = theme.primary) =>
  React.cloneElement(icon, {
    style: { fontSize: 16, color, marginRight: 0 },
  });

const SignIn = () => {
  const { setIsLoggedIn, setUserProfile, isLoggedIn } = useContext(AuthContext);
  const { setRestaurant } = useContext(AppStateContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { request } = useRequest();
  const screens = useBreakpoint();
  const isAdminLoginRoute = location.pathname === "/admin/login";
  const allowedRoles = isAdminLoginRoute ? ["admin"] : ["hotel", /* "agent", */ "airline", "bus"];

  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [forgotVisible, setForgotVisible] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);
  const [forgotLoading, setForgotLoading] = useState(false);

  const [activeRole, setActiveRole] = useState(
    isAdminLoginRoute ? "admin" : "hotel"
  );
  const [showPassword, setShowPassword] = useState(false);

  const [form] = Form.useForm();
  const [forgotForm] = Form.useForm();
  const [resetForm] = Form.useForm();

  useEffect(() => {
    const from = location.state?.from;
    if (from && from.pathname) {
      if (from.pathname.startsWith("/hotel-management/") && (from.search || "").includes("tab=rooms")) {
        setActiveRole("airline");
      }
    }
  }, [location.state]);

  const handleCloseModal = () => {
    setForgotVisible(false);
    setForgotStep(1);
    setForgotLoading(false);
    forgotForm.resetFields();
    resetForm.resetFields();
  };

  const handleSendOTP = (values) => {
    const { email } = values;

    setForgotLoading(true);
    request({
      url: apiPath.adminForgotPassword,
      method: "POST",
      data: { email },
      onSuccess: (data) => {
        setForgotLoading(false);
        if (data?.success) {
          ShowToast(data?.message || "OTP sent successfully to your email", Severty.SUCCESS);
          setForgotStep(2);
        } else {
          ShowToast(data?.message || "Failed to send OTP", Severty.ERROR);
        }
      },
      onError: (err) => {
        setForgotLoading(false);
        const errMsg = err?.response?.data?.message || "Failed to send OTP";
        ShowToast(errMsg, Severty.ERROR);
      }
    });
  };

  const handleResetPassword = (values) => {
    const { otp, newPassword, confirmPassword } = values;
    const email = forgotForm.getFieldValue("email");

    setForgotLoading(true);
    request({
      url: apiPath.adminResetPassword,
      method: "POST",
      data: {
        email,
        otp,
        newPassword,
        confirmPassword
      },
      onSuccess: (data) => {
        setForgotLoading(false);
        if (data?.success) {
          ShowToast(data?.message || "Password reset successfully", Severty.SUCCESS);
          handleCloseModal();
        } else {
          ShowToast(data?.message || "Failed to reset password", Severty.ERROR);
        }
      },
      onError: (err) => {
        setForgotLoading(false);
        const errMsg = err?.response?.data?.message || "Failed to reset password";
        ShowToast(errMsg, Severty.ERROR);
      }
    });
  };

  const onSubmit = (values) => {
    const { email, password } = values;
    const roleConfig = ROLE_CONFIG[activeRole] || ROLE_CONFIG.admin;

    if (!email || !password) {
      return ShowToast("Email & Password required", Severty.ERROR);
    }

    setLoading(true);

    request({
      url: roleConfig.loginPath,
      method: "POST",
      data: { email, password, userType: roleConfig.userType },
      onSuccess: (data) => {
        setLoading(false);

        if (data?.success || data?.status) {
          handleLoginSuccess(data, values);
        } else {
          ShowToast(data?.message || "Login failed", Severty.ERROR);
        }
      },
      onError: (err) => {
        setLoading(false);
        const raw = err?.response?.data?.message;
        const msg =
          typeof raw === "string" && raw.trim()
            ? raw
            : "Login failed";
        ShowToast(msg, Severty.ERROR);
      },
    });
  };

  const handleLoginSuccess = (data, values) => {
    const roleConfig = ROLE_CONFIG[activeRole] || ROLE_CONFIG.admin;
    localStorage.setItem("loginData", JSON.stringify(data));
    localStorage.setItem("authRole", roleConfig.key);
    const token = extractToken(data);
    if (token) {
      localStorage.setItem("token", token);
    }

    const user = resolveUserDetails(data);

    if (user) {
      localStorage.setItem("userProfile", JSON.stringify(user));
      setUserProfile(user);
    }

    if (rememberMe) {
      localStorage.setItem("rememberMe", "true");
      localStorage.setItem(
        "email",
        encryptDecrypt.encryptEmail(values.email)
      );
      localStorage.setItem(
        "password",
        encryptDecrypt.encryptPassword(values.password)
      );
    } else {
      localStorage.removeItem("rememberMe");
      localStorage.removeItem("email");
      localStorage.removeItem("password");
    }

    setIsLoggedIn(true);

    setRestaurant({
      name: user?.name || "",
      ratings: 2,
      review: 1,
    });

    ShowToast(data?.message || "Login successful", Severty.SUCCESS);

    const from = location.state?.from;
    let destination = roleConfig.homePath;
    if (from && from.pathname) {
      destination = from.pathname + (from.search || "") + (from.hash || "");
    }
    navigate(destination, { replace: true });
  };

  useEffect(() => {
    const token = localStorage.getItem("loginData");
    const storedRole = localStorage.getItem("authRole") || "admin";
    const from = location.state?.from;
    let destination = ROLE_CONFIG[storedRole]?.homePath || "/dashboard";
    if (from && from.pathname) {
      destination = from.pathname + (from.search || "") + (from.hash || "");
    }

    if (token && isLoggedIn) {
      navigate(destination, { replace: true });
    }

    const isRemembered = localStorage.getItem("rememberMe") === "true";
    const savedEmail = localStorage.getItem("email");
    const savedPassword = localStorage.getItem("password");

    if (isRemembered && savedEmail && savedPassword) {
      try {
        const decryptedEmail = encryptDecrypt.decryptEmail(savedEmail);
        const decryptedPassword = encryptDecrypt.decryptPassword(savedPassword);

        if (decryptedEmail && decryptedPassword) {
          form.setFieldsValue({
            email: decryptedEmail,
            password: decryptedPassword,
          });
          setRememberMe(true);
        }
      } catch (error) {
        console.error("Error decrypting remembered credentials:", error);
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("email");
        localStorage.removeItem("password");
      }
    }
  }, []);

  useEffect(() => {
    const defaultRole = isAdminLoginRoute ? "admin" : "hotel";
    if (!allowedRoles.includes(activeRole)) {
      setActiveRole(defaultRole);
    }
  }, [isAdminLoginRoute, activeRole]);

  const pad = screens.xs ? 16 : 24;
  const roleConfig = ROLE_CONFIG[activeRole] || ROLE_CONFIG.admin;

  const fieldLabel = (icon, text) => (
    <span className="sign-in-label">
      {icon}
      <span>{text}</span>
      <span className="sign-in-label__asterisk">*</span>
    </span>
  );

  return (
    <div
      className="sign-in-page"
      style={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: pad,
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(135deg, #132272 0%, #3550DC 55%, #667eea 100%)",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `radial-gradient(circle at 15% 85%, rgba(255,255,255,0.14) 0%, transparent 40%),
            radial-gradient(circle at 85% 15%, rgba(255,255,255,0.10) 0%, transparent 45%)`,
          pointerEvents: "none",
        }}
      />

      <Card
        bordered={false}
        className="sign-in-card"
        style={{
          width: "100%",
          maxWidth: 560,
          borderRadius: 20,
          boxShadow:
            "0 6px 18px rgba(20, 44, 74, 0.12), 0 26px 64px rgba(20, 44, 74, 0.20)",
          border: "1px solid rgba(255,255,255,0.35)",
          background: "rgba(255, 255, 255, 0.96)",
          backdropFilter: "blur(6px)",
          position: "relative",
          zIndex: 1,
          overflow: "hidden",
        }}
        bodyStyle={{
          padding: screens.xs ? "28px 22px 32px" : "40px 36px 36px",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
          }}
        />

        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              borderRadius: 999,
              border: `1px solid ${theme.border}`,
              background: "rgba(53, 80, 220, 0.06)",
              marginBottom: 14,
            }}
          >
            <SafetyCertificateOutlined style={{ color: theme.primary, fontSize: 14 }} />
            <Text style={{ fontSize: 12, fontWeight: 600, color: theme.primary }}>
              Secure Sign In
            </Text>
          </div>
          <div
            style={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              boxShadow: "0 10px 28px rgba(53, 80, 220, 0.35)",
            }}
          >
            <SafetyCertificateOutlined style={{ fontSize: 36, color: "#fff" }} />
          </div>
          <Title
            level={3}
            style={{
              margin: 0,
              color: "rgba(0, 0, 0, 0.88)",
              fontWeight: 800,
              fontSize: screens.xs ? 22 : 26,
              letterSpacing: "-0.02em",
            }}
          >
            {roleConfig.title}
          </Title>
          <Paragraph
            type="secondary"
            style={{
              margin: "8px 0 0",
              fontSize: 14,
              marginBottom: 0,
            }}
          >
            {roleConfig.subtitle}
          </Paragraph>
          <Text type="secondary" style={{ fontSize: 12, marginTop: 6, display: "block" }}>
            Hotel Management System
          </Text>
        </div>

        {allowedRoles.length > 1 && (
          <Tabs
            activeKey={activeRole}
            onChange={setActiveRole}
            centered
            className="role-tabs"
            items={allowedRoles.map((roleKey) => ({
              key: roleKey,
              label: ROLE_CONFIG[roleKey].label,
            }))}
            style={{ marginBottom: 18 }}
          />
        )}

        <Form
          layout="vertical"
          onFinish={onSubmit}
          form={form}
          requiredMark={false}
          className="sign-in-form"
          autoComplete="off"
        >
          <Form.Item
            name="email"
            label={fieldLabel(
              labelIcon(<UserOutlined />),
              roleConfig.emailLabel
            )}
            rules={[
              { required: true, message: `Please enter ${roleConfig.label.toLowerCase()} email` },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              size="large"
              placeholder={roleConfig.emailPlaceholder}
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={fieldLabel(
              labelIcon(<LockOutlined />),
              "Password"
            )}
            rules={[{ required: true, message: "Please enter password" }]}
            style={{ marginBottom: 20 }}
          >
            <Input
              size="large"
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              autoComplete="current-password"
              suffix={
                <button
                  type="button"
                  className="sign-in-view-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </button>
              }
            />
          </Form.Item>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <Checkbox
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="sign-in-remember"
            >
              Remember me
            </Checkbox>
            {activeRole === "admin" && (
              <button
                type="button"
                onClick={() => setForgotVisible(true)}
                style={{
                  background: "none",
                  border: "none",
                  color: theme.primary,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 600,
                  padding: 0,
                  transition: "color 0.2s ease"
                }}
                className="forgot-password-link"
              >
                Forgot password?
              </button>
            )}

          </div>

          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            block
            size="large"
            className="sign-in-submit"
            style={{
              height: 48,
              borderRadius: 12,
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "0.2px",
              background: theme.primary,
              borderColor: theme.primary,
              boxShadow: "0 10px 24px rgba(31, 78, 121, 0.32)",
            }}
          >
            {loading ? "Authenticating..." : roleConfig.submitText}
          </Button>
        </Form>

        {/* <div
          style={{
            textAlign: "center",
            marginTop: 28,
            paddingTop: 20,
            borderTop: `1px solid ${theme.border}`,
          }}
        >
          <Space size={8}>
            <SafetyCertificateOutlined
              style={{ color: "#52c41a", fontSize: 14 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Secured with 256-bit encryption
            </Text>
          </Space>
        </div> */}
      </Card>

      <Modal
        title={
          <Title level={4} style={{ margin: 0, color: theme.primary }}>
            {forgotStep === 1 ? "Forgot Password" : "Reset Password"}
          </Title>
        }
        visible={forgotVisible}
        onCancel={handleCloseModal}
        footer={null}
        centered
        destroyOnClose
      >
        <div style={{ padding: "8px 0" }}>
          {forgotStep === 1 ? (
            <Form
              layout="vertical"
              form={forgotForm}
              onFinish={handleSendOTP}
              requiredMark={false}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  Please enter your registered admin email address. We will send you a 6-digit OTP to reset your password.
                </Paragraph>
                <Form.Item
                  name="email"
                  label={<Text strong style={{ display: "block" }}>Email Address</Text>}
                  rules={[
                    { required: true, message: "Please enter email address" },
                    { type: "email", message: "Please enter a valid email address" }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    size="large"
                    placeholder="admin@example.com"
                    disabled={forgotLoading}
                    prefix={<UserOutlined style={{ color: "rgba(0,0,0,0.25)" }} />}
                  />
                </Form.Item>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                  <Button onClick={handleCloseModal} disabled={forgotLoading}>
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={forgotLoading}
                    style={{
                      background: theme.primary,
                      borderColor: theme.primary,
                    }}
                  >
                    Send OTP
                  </Button>
                </div>
              </div>
            </Form>
          ) : (
            <Form
              layout="vertical"
              form={resetForm}
              onFinish={handleResetPassword}
              requiredMark={false}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Paragraph type="secondary" style={{ margin: 0 }}>
                  An OTP has been sent to <strong>{forgotForm.getFieldValue("email")}</strong>. Please enter the OTP and configure your new password.
                </Paragraph>
                <Form.Item
                  name="otp"
                  label={<Text strong style={{ display: "block" }}>OTP Code</Text>}
                  rules={[
                    { required: true, message: "Please enter the OTP" },
                    { len: 6, message: "OTP must be exactly 6 digits" }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input
                    size="large"
                    placeholder="Enter 6-digit OTP"
                    disabled={forgotLoading}
                    maxLength={6}
                  />
                </Form.Item>
                <Form.Item
                  name="newPassword"
                  label={<Text strong style={{ display: "block" }}>New Password</Text>}
                  rules={[
                    {
                      required: true,
                      message: "Please enter your password",
                    },
                    {
                      min: 8,
                      message: "Password must be at least 8 characters long",
                    },
                    {
                      pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
                      message:
                        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
                    }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input.Password
                    size="large"
                    placeholder="Enter new password"
                    disabled={forgotLoading}
                  />
                </Form.Item>
                <Form.Item
                  name="confirmPassword"
                  label={<Text strong style={{ display: "block" }}>Confirm New Password</Text>}
                  dependencies={['newPassword']}
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your password",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('newPassword') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Passwords do not match'));
                      },
                    }),
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input.Password
                    size="large"
                    placeholder="Confirm new password"
                    disabled={forgotLoading}
                  />
                </Form.Item>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
                  <Button
                    onClick={() => setForgotStep(1)}
                    disabled={forgotLoading}
                    type="link"
                    style={{ padding: 0, color: theme.primary }}
                  >
                    Back to Email
                  </Button>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Button onClick={handleCloseModal} disabled={forgotLoading}>
                      Cancel
                    </Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={forgotLoading}
                      style={{
                        background: theme.primary,
                        borderColor: theme.primary,
                      }}
                    >
                      Reset Password
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </div>
      </Modal>

      <style>{`
        .sign-in-page .sign-in-label {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          color: rgba(0, 0, 0, 0.85);
          font-size: 14px;
        }
        .sign-in-page .forgot-password-link:hover {
          color: var(--theme1Dark, #132272) !important;
          text-decoration: underline;
        }
        .sign-in-page .sign-in-label__asterisk {
          color: #ff4d4f;
          font-size: 14px;
          line-height: 1;
        }
        .sign-in-page .sign-in-form .ant-form-item-label {
          padding-bottom: 6px !important;
        }
        .sign-in-page .sign-in-form .ant-form-item {
          margin-bottom: 18px !important;
        }
        .sign-in-page .ant-input,
        .sign-in-page .ant-input-password,
        .sign-in-page .ant-input-affix-wrapper {
          border-radius: 12px !important;
          font-size: 15px !important;
          border: 1px solid ${theme.border} !important;
          background: #fff !important;
          transition: border-color 0.2s ease, box-shadow 0.2s ease !important;
        }
        .sign-in-page .ant-input-affix-wrapper {
          padding: 0 0 0 12px !important;
          height: 48px !important;
          display: flex !important;
          align-items: center !important;
        }
        .sign-in-page .ant-input-affix-wrapper .ant-input {
          box-shadow: none !important;
          height: 46px !important;
          padding-top: 0 !important;
          padding-bottom: 0 !important;
        }
        .sign-in-page .ant-input-password {
          padding: 0 !important;
        }
        .sign-in-page .ant-input-password .ant-input {
          height: 46px !important;
        }
        .sign-in-page .ant-input-affix-wrapper:hover,
        .sign-in-page .ant-input:hover {
          border-color: rgba(53, 80, 220, 0.45) !important;
        }
        .sign-in-page .ant-input-affix-wrapper-focused,
        .sign-in-page .ant-input:focus {
          border-color: var(--theme1, #3550DC) !important;
          box-shadow: 0 0 0 3px rgba(53, 80, 220, 0.15) !important;
        }
        .sign-in-page .ant-input-password-icon {
          color: rgba(0, 0, 0, 0.35) !important;
          padding: 0 10px !important;
          margin: 0 !important;
          width: auto !important;
          height: auto !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          border-radius: 0 !important;
          background: transparent !important;
          font-size: 16px !important;
          opacity: 1 !important;
        }
        .sign-in-page .ant-input-suffix {
          margin: 0 !important;
        }
        .sign-in-page .sign-in-view-btn {
          border: none;
          background: transparent;
          color: var(--theme1, #3550DC);
          font-size: 16px;
          cursor: pointer;
          padding: 0 4px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .sign-in-page .sign-in-view-btn:hover {
          color: var(--theme1Dark, #132272);
        }
        .sign-in-page .sign-in-remember.ant-checkbox-wrapper {
          font-size: 14px;
        }
        .sign-in-page .sign-in-remember .ant-checkbox-checked .ant-checkbox-inner {
          background-color: var(--theme1, #3550DC) !important;
          border-color: var(--theme1, #3550DC) !important;
        }
        .sign-in-page .sign-in-submit.ant-btn-primary:hover,
        .sign-in-page .sign-in-submit.ant-btn-primary:focus {
          background: var(--theme1Dark, #132272) !important;
          border-color: var(--theme1Dark, #132272) !important;
        }
        .sign-in-page .ant-card-body {
          position: relative;
        }
        .sign-in-page .ant-card-body::after {
          content: "";
          position: absolute;
          right: -80px;
          top: -80px;
          width: 220px;
          height: 220px;
          background: radial-gradient(circle, rgba(53, 80, 220, 0.08) 0%, rgba(53, 80, 220, 0) 70%);
          pointer-events: none;
        }
        .sign-in-page .role-tabs .ant-tabs-nav {
          margin: 0 !important;
        }
        .sign-in-page .role-tabs .ant-tabs-nav::before {
          border-bottom: none !important;
        }
        .sign-in-page .role-tabs .ant-tabs-nav-list {
          width: 100%;
          background: rgba(53, 80, 220, 0.08);
          border: 1px solid rgba(53, 80, 220, 0.18);
          border-radius: 12px;
          padding: 4px;
          gap: 4px;
        }
        .sign-in-page .role-tabs .ant-tabs-tab {
          flex: 1 1 0;
          margin: 0 !important;
          padding: 0 !important;
          border-radius: 9px;
          transition: all 0.2s ease;
        }
        .sign-in-page .role-tabs .ant-tabs-tab .ant-tabs-tab-btn {
          width: 100%;
          text-align: center;
          font-size: 13px;
          font-weight: 600;
          color: rgba(0, 0, 0, 0.58);
          padding: 8px 0;
          line-height: 1.15;
        }
        .sign-in-page .role-tabs .ant-tabs-tab:hover .ant-tabs-tab-btn {
          color: var(--theme1, #3550DC) !important;
        }
        .sign-in-page .role-tabs .ant-tabs-tab-active {
          background: #fff;
          box-shadow: 0 2px 8px rgba(19, 34, 114, 0.12);
        }
        .sign-in-page .role-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: var(--theme1, #3550DC) !important;
        }
        .sign-in-page .role-tabs .ant-tabs-ink-bar {
          display: none !important;
        }
        @media (max-width: 576px) {
          .sign-in-page .ant-input-affix-wrapper {
            height: 44px !important;
          }
          .sign-in-page .ant-input-affix-wrapper .ant-input {
            height: 42px !important;
          }
          .sign-in-page .role-tabs .ant-tabs-tab .ant-tabs-tab-btn {
            font-size: 12px;
            padding: 7px 0;
          }
        }
      `}</style>
    </div>
  );
};

export default SignIn;
